
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Book, CalendarClock, FolderGit2, MessageSquare, Users } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import RoleDebugger from '@/components/debug/RoleDebugger';

const MentorDashboard = () => {
  const { user, isMentor, userRoles } = useAuth();
  const navigate = useNavigate();
  
  // Debug log for roles
  useEffect(() => {
    console.log("Auth check in MentorDashboard:", { 
      user: user?.id, 
      isMentor: isMentor(), 
      roles: userRoles 
    });
  }, [user, isMentor, userRoles]);
  
  useEffect(() => {
    // Redirect if not a mentor
    if (user && !isMentor()) {
      console.log("User is not a mentor, redirecting to dashboard");
      navigate('/dashboard');
    }
  }, [isMentor, navigate, user]);

  // Fetch counts for dashboard cards
  const { data: moduleReviewCount } = useQuery({
    queryKey: ['mentor-modules-review-count'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('course_build_status')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'in_review');
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching modules in review count:', error);
        return 0;
      }
    }
  });

  const { data: myOfficeHoursCount } = useQuery({
    queryKey: ['mentor-office-hours-count'],
    queryFn: async () => {
      if (!user) return 0;
      try {
        const { count, error } = await supabase
          .from('office_hours')
          .select('*', { count: 'exact', head: true })
          .eq('mentor_id', user.id);
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching mentor office hours count:', error);
        return 0;
      }
    }
  });

  const { data: studentsCount } = useQuery({
    queryKey: ['mentor-students-count'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('user_roles')
          .select('*', { count: 'exact', head: true })
          .eq('role', 'student');
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching students count:', error);
        return 0;
      }
    }
  });

  const { data: projectsForReviewCount } = useQuery({
    queryKey: ['mentor-projects-for-review-count'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .not('submission_url', 'is', null)
          .is('score', null);
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error('Error fetching projects for review count:', error);
        return 0;
      }
    }
  });

  const mentorCards = [
    {
      title: 'Course Builder',
      description: 'Create and review modules and lessons',
      icon: <Book className="h-8 w-8 text-green-500 dark:text-green-400" />,
      action: () => navigate('/mentor/course-builder'),
      count: moduleReviewCount,
      badge: moduleReviewCount && moduleReviewCount > 0 ? 'Needs Review' : undefined
    },
    {
      title: 'Office Hours',
      description: 'Manage your office hours sessions',
      icon: <CalendarClock className="h-8 w-8 text-purple-500 dark:text-purple-400" />,
      action: () => navigate('/mentor/office-hours'),
      count: myOfficeHoursCount
    },
    {
      title: 'Project Reviews',
      description: 'Review and provide feedback on student projects',
      icon: <FolderGit2 className="h-8 w-8 text-amber-500 dark:text-amber-400" />,
      action: () => navigate('/mentor/projects'),
      count: projectsForReviewCount,
      badge: projectsForReviewCount && projectsForReviewCount > 0 ? 'Needs Review' : undefined
    },
    {
      title: 'Students',
      description: 'View and track student progress',
      icon: <Users className="h-8 w-8 text-blue-500 dark:text-blue-400" />,
      action: () => navigate('/mentor/students'),
      count: studentsCount
    }
  ];

  return (
    <div>
      <RoleDebugger />
      
      <div className="mb-8">
        <h1 className="text-3xl font-bold section-header">Mentor Dashboard</h1>
        <p className="text-muted-foreground section-subheader">Monitor and guide your students</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {mentorCards.map((card, index) => (
          <Card key={index} className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl font-bold card-title">{card.title}</CardTitle>
                  {card.badge && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1">
                      {card.badge}
                    </span>
                  )}
                </div>
                {card.icon}
              </div>
              <CardDescription>{card.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold dark:text-white">{card.count}</div>
            </CardContent>
            <CardFooter className="bg-muted/50 pt-2 dark:bg-muted/20">
              <Button 
                variant="outline" 
                className="w-full dark:border-gray-700 dark:hover:bg-gray-800" 
                onClick={card.action}
              >
                Manage
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MentorDashboard;
